import {
  type DataRootCheckStatus,
  type DataRootMode,
  MAX_MOUNT_LIST_ENTRIES,
  type ReplaceRunEnvironmentDataRootsRequestBody,
  type RunEnvironmentDataRoot,
  validateDataRootPath,
} from "common";

/**
 * One editable row of the Data folders card. `mode` is carried, never shown: an existing root
 * keeps whatever the workspace mount allowlist gave it, and a new row is written as read (spec
 * section 11).
 */
export interface DataFolderRow {
  path: string;
  label: string;
  mode: DataRootMode;
}

export const MAX_DATA_FOLDER_LABEL_LENGTH = 256;

export const rowsFromRoots = (
  roots: RunEnvironmentDataRoot[]
): DataFolderRow[] =>
  roots.map(root => ({
    path: root.path,
    label: root.label ?? "",
    mode: root.mode,
  }));

export const newDataFolderRow = (): DataFolderRow => ({
  path: "",
  label: "",
  mode: "read",
});

/**
 * The server's rules (`checkDataRootList`), mirrored so the owner sees them before saving. The
 * collision key is the database's: case insensitive over the first 700 characters. A folder may be
 * absolute or home relative (`~`, `~/<path>`, agent-6-1); a `~` row is kept as typed and each
 * user's agent resolves it against its own home.
 */
export const validateDataFolderRows = (
  rows: DataFolderRow[]
): (string | null)[] => {
  const seen = new Map<string, number>();
  return rows.map((row, index) => {
    if (index >= MAX_MOUNT_LIST_ENTRIES)
      return `At most ${MAX_MOUNT_LIST_ENTRIES} folders can be declared.`;
    if (row.path.length === 0) {
      return "Enter an absolute folder path, or ~/<path> for a folder in each user's home.";
    }
    const check = validateDataRootPath(row.path);
    if (check.kind === "refused") return check.detail;
    if (row.label.length > MAX_DATA_FOLDER_LABEL_LENGTH) {
      return `Labels are at most ${MAX_DATA_FOLDER_LABEL_LENGTH} characters.`;
    }
    const key = row.path.slice(0, 700).toLowerCase();
    const earlier = seen.get(key);
    if (earlier !== undefined) return `Same folder as row ${earlier + 1}.`;
    seen.set(key, index);
    return null;
  });
};

export const toReplaceBody = (
  rows: DataFolderRow[]
): ReplaceRunEnvironmentDataRootsRequestBody => ({
  roots: rows.map(row => ({
    path: row.path,
    mode: row.mode,
    label: row.label.trim() === "" ? null : row.label.trim(),
  })),
});

/** The environment's own directories a suggested data folder row is taken from. */
export interface EnvironmentDirectories {
  defWorkdir?: string | null;
  defNextflowWorkdir?: string | null;
  singuCache?: string | null;
}

/**
 * Rows the card prefills when the environment has no saved data folders: its execution directory
 * and default work directory (read_write, runs write there), and the folder holding its container
 * images (read). Form only: nothing is stored until the owner saves. A value the server would
 * refuse is left out rather than suggested, and so are `/` and a bare `~`, which would hand over
 * the whole host or the whole home. The first row naming a folder wins, so a folder that is both
 * the execution directory and the image folder stays read_write.
 */
export const suggestedDataFolderRows = (
  directories: EnvironmentDirectories
): DataFolderRow[] => {
  const candidates: DataFolderRow[] = [
    {
      path: directories.defWorkdir ?? "",
      label: "Execution directory",
      mode: "read_write",
    },
    {
      path: directories.defNextflowWorkdir ?? "",
      label: "Work directory",
      mode: "read_write",
    },
    {
      path: directories.singuCache ?? "",
      label: "Container images",
      mode: "read",
    },
  ];
  const seen = new Set<string>();
  const rows: DataFolderRow[] = [];
  for (const candidate of candidates) {
    const path = candidate.path.trim().replace(/(.)\/+$/, "$1");
    if (path === "" || path === "/" || path === "~") continue;
    if (validateDataRootPath(path).kind === "refused") continue;
    const key = path.slice(0, 700).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ ...candidate, path });
  }
  return rows;
};

/**
 * Whether the rows say exactly what is saved. Every data folder PUT replaces the whole set and
 * bumps the environment's access configuration version, which can turn pending approvals
 * review_stale, so a save that changes nothing is not offered.
 */
export const sameDataFolderRows = (
  rows: DataFolderRow[],
  saved: DataFolderRow[]
): boolean =>
  rows.length === saved.length &&
  rows.every(
    (row, index) =>
      row.path === saved[index].path &&
      row.mode === saved[index].mode &&
      row.label.trim() === saved[index].label.trim()
  );

export const dataFoldersCardSummary = (
  roots?: RunEnvironmentDataRoot[]
): string => {
  if (!roots) return "";
  if (roots.length === 0) return "No folders";
  return roots.length === 1 ? "1 folder" : `${roots.length} folders`;
};

export const CHECK_STATUS_TEXT: Record<DataRootCheckStatus, string> = {
  ok: "Readable",
  missing: "Does not exist",
  not_a_directory: "Not a folder",
  not_listable: "Cannot be listed",
  error: "Could not be checked",
};

export const backsDataSourcesWarning = (
  count: number,
  unchecked = 0
): string => {
  const sources = (n: number): string =>
    `${n} data ${n === 1 ? "source" : "sources"}`;
  const parts: string[] = [];
  if (count > 0) {
    parts.push(
      `A folder you removed or changed backs ${sources(count)}. ` +
        "Files already in datasets keep working, but browsing those sources and adding more files from them will be refused."
    );
  }
  if (unchecked > 0) {
    // agent-6-1: a ~ folder decides these, and their owners' agents have not reported a home yet.
    parts.push(
      `${count > 0 ? `${unchecked} more data ${unchecked === 1 ? "source" : "sources"}` : sources(unchecked)} could not be checked: ` +
        "they sit under a folder starting with ~ whose owner's agent has not reported its home. They may stop working after this change."
    );
  }
  return parts.join(" ");
};
